import React from 'react';
import { useLocation } from 'react-router-dom';
import BottomNavBar from './BottomNavBar';
import { AnimatePresence, motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { getLocalizedPrice } from '../lib/pricing';
import { supabase } from '../lib/supabase';
import { useEffect, useState } from 'react';

interface AppShellProps {
  isAuthenticated: boolean;
  isAdmin: boolean;
  children: React.ReactNode;
  hasLifetimeAccess?: boolean;
  onUpgradeClick?: () => void;
  isUpgrading?: boolean;
}

interface BannerContent {
  original_price: string;
  current_price: string;
  message: string;
  cta: string;
}

const AppShell: React.FC<AppShellProps> = ({
  isAuthenticated,
  isAdmin,
  children,
  hasLifetimeAccess = false,
  onUpgradeClick,
  isUpgrading = false
}) => {
  const location = useLocation();
  const { symbol } = getLocalizedPrice();
  const [bannerContent, setBannerContent] = useState<BannerContent>({
    original_price: '18.99',
    current_price: '8.99',
    message: 'Special Offer!',
    cta: 'Upgrade Now'
  });

  useEffect(() => {
    const loadBannerContent = async () => {
      const { data, error } = await supabase
        .from('landing_content')
        .select('key, content')
        .eq('section', 'banner');

      if (error) {
        console.error('Error loading banner content:', error);
        return;
      }

      if (data && data.length > 0) {
        const content = data.reduce((acc: any, item) => {
          acc[item.key] = item.content;
          return acc;
        }, {});
        setBannerContent(content as BannerContent);
      }
      // If no data is found, we'll keep using the default banner content
    };

    loadBannerContent();

    // Subscribe to realtime changes
    const subscription = supabase
      .channel('banner_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'landing_content',
          filter: 'section=eq.banner'
        },
        loadBannerContent
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [isAuthenticated]); // Add isAuthenticated to dependencies

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50/50 to-pink-50/50">
      {isAuthenticated && !hasLifetimeAccess && (
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 text-center relative">
          <div className="max-w-4xl mx-auto flex items-center justify-center space-x-4">
            <div className="flex items-center">
              <Sparkles className="h-5 w-5 mr-2" />
              <span className="font-medium">{bannerContent.message}</span>
            </div>
            <div className="text-sm">
              <span className="line-through opacity-75">{symbol}{bannerContent.original_price}</span>
              {' → '}
              <span className="font-bold text-lg">{symbol}{bannerContent.current_price}</span>
              {' for lifetime access'}
            </div>
            <button
              onClick={onUpgradeClick}
              disabled={isUpgrading}
              className="bg-white text-purple-600 px-4 py-1 rounded-full text-sm font-medium hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpgrading ? 'Loading...' : bannerContent.cta}
            </button>
          </div>
        </div>
      )}
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
          className={`min-h-screen pb-24 ${isAuthenticated && !hasLifetimeAccess ? 'pt-12' : ''} safe-area-inset-top`}
        >
          {children}
        </motion.main>
      </AnimatePresence>
      
      <BottomNavBar isAuthenticated={isAuthenticated} isAdmin={isAdmin} />
    </div>
  );
};

export default AppShell;